import { z } from 'zod';
import { imageUploadField } from '../MessageEditor/MessageImageUpload';
import * as ActionDialogForm from './actionSettingFormValue';
import { MessageTemplate, MessageTemplateSchema } from './messageTemplate';

export type FileBag = {
  thumbFile: File | undefined;
  userListFile: File | undefined;
  actionImages: Record<string, File>;
};

export type RecipientCountInfo =
  | { status: 'calculating' }
  | {
      status: 'estimated' | 'determined';
      count: number;
    };

export type TargetType = 'designated_user' | 'all_user' | 'tagged_user';

export const recipientCountInfoSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('calculating') }),
  z.object({
    status: z.enum(['estimated', 'determined']),
    count: z.number().int().nonnegative(),
  }),
]);

export const MessageUserInputSchema = z.object({
  messageCategoryKey: z.string({ error: 'missing messageCategoryId' }),
  name: MessageTemplateSchema.shape['name'],
  title: MessageTemplateSchema.shape['title'],
  summary: MessageTemplateSchema.shape['summary'],
  thumbUrl: MessageTemplateSchema.shape['thumbUrl'],
  scheduleAt: MessageTemplateSchema.shape['scheduleAt'],
  actionSetting: MessageTemplateSchema.shape['actionSetting'],
  notificationSettings: MessageTemplateSchema.shape['notificationSettings'],
  segment: MessageTemplateSchema.shape['segment'],
});

export type MessageUserInput = z.infer<typeof MessageUserInputSchema>;

const SegmentSettingFormValueSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('list_segment'), file: z.file().optional() }),
  z.object({
    type: z.literal('condition_segment'),
    condition: z.enum(['all_user']),
  }),
]);

const ValidSegmentSettingFormValueSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('list_segment'),
    file: z
      .file({ error: '檔案不可為空' })
      .mime('text/csv', { error: '檔案需為 csv 檔' }),
  }),
  z.object({
    type: z.literal('condition_segment'),
    condition: z.enum(['all_user']),
  }),
]);

export const MessageFormValueSchema = z.object({
  messageCategoryKey: z.string().nullable(),
  name: z.string(),
  title: z.string(),
  summary: z.string(),
  thumbUrl: imageUploadField.schema,
  scheduleAt: z.date().optional(),
  actionSetting: ActionDialogForm.ActionSettingFormValueSchema,
  notificationSettings: MessageTemplateSchema.shape['notificationSettings'],
  segment: SegmentSettingFormValueSchema.nullish(),
});

const HIDDEN_ERROR = ' ';

export const ValidMessageFormValueSchema = z.object({
  messageCategoryKey: z.string({ error: HIDDEN_ERROR }),
  name: z.string().min(1, { error: HIDDEN_ERROR }),
  title: z.string().min(1, { error: HIDDEN_ERROR }),
  summary: z.string().min(1, { error: HIDDEN_ERROR }),
  thumbUrl: imageUploadField.validSchema,
  scheduleAt: z.date({ error: '日期不得為空' }),
  actionSetting: ActionDialogForm.ValidActionSettingFormValueSchema,
  notificationSettings: MessageTemplateSchema.shape['notificationSettings'],
  segment: ValidSegmentSettingFormValueSchema,
});

export type MessageFormValue = z.infer<typeof MessageFormValueSchema>;
export type ValidMessageFormValue = z.infer<typeof ValidMessageFormValueSchema>;

export const createMessageInitFormValues = () => {
  return {
    messageCategoryKey: null,
    name: '新訊息模版',
    title: '',
    summary: '',
    notificationSettings: {
      notification: {
        push_enabled: false,
      },
    },
    segment: {
      type: 'condition_segment',
      condition: 'all_user',
    },
    scheduleAt: new Date(),
    thumbUrl: imageUploadField.init(null),
    // TODO change to  ActionDialogForm default init value
    actionSetting: ActionDialogForm.actionListDialogFormValueInit,
  } as unknown as ValidMessageFormValue;
};

// invalid 怎麼辦
export const toMessageUserInput = (
  input: MessageFormValue,
): { payload: MessageUserInput; files: FileBag } => {
  const files: FileBag = {
    actionImages: {},
    thumbFile: undefined,
    userListFile: undefined,
  };

  const thumbRef = imageUploadField.toWriteRef(input.thumbUrl);
  const thumbUrl = thumbRef.kind === 'url' ? thumbRef.url : null;
  if (thumbRef.kind === 'file') files.thumbFile = thumbRef.file;

  const segmentInput = input.segment;
  const segment = !segmentInput
    ? null
    : segmentInput.type === 'list_segment'
      ? { type: 'list_segment' }
      : { type: 'condition_segment', condition: segmentInput.condition };

  if (segmentInput?.type === 'list_segment' && segmentInput.file) {
    files.userListFile = segmentInput.file;
  }

  let keyCounter = 0;
  const nextKey = () => `img_${++keyCounter}`;
  const resolveImage: ActionDialogForm.ImageResolver = (field) => {
    const ref = imageUploadField.toWriteRef(field);
    if (ref.kind === 'url') return ref.url;
    if (ref.kind === 'file') {
      const key = nextKey();
      files.actionImages[key] = ref.file;

      return `@upload:${key}`;
    }
    return '';
    // 事實上，空字串還是非法值。目前還沒有一個自恰的邏輯避免 '' 這個狀況
  };

  const parsedMessageUserInput = MessageUserInputSchema.parse(
    {
      messageCategoryKey: input.messageCategoryKey,
      name: input.name,
      title: input.title,
      summary: input.summary,
      notificationSettings: input.notificationSettings,
      segment,
      thumbUrl,
      scheduleAt: input.scheduleAt ? input.scheduleAt.toISOString() : null,
      actionSetting: ActionDialogForm.toActionSetting(
        input.actionSetting,
        resolveImage,
      ),
    },
    { reportInput: true },
  );

  return { payload: parsedMessageUserInput, files };
};

export const fromUserInputToFormValue = (
  input: MessageUserInput,
): MessageFormValue => {
  const parsed = MessageFormValueSchema.parse({
    messageCategoryKey: input.messageCategoryKey,
    name: input.name,
    title: input.title,
    summary: input.summary,
    notificationSettings: input.notificationSettings,
    segment: input.segment,
    thumbUrl: imageUploadField.init(input.thumbUrl),
    scheduleAt: input?.scheduleAt ? new Date(input.scheduleAt) : undefined,
    actionSetting: ActionDialogForm.toFormValue(input.actionSetting),
  });

  return parsed;
};

export const toMessageFormValue = (
  input: MessageTemplate,
): MessageFormValue => {
  const segmentInput = input.segment;

  const segmentSetting: MessageFormValue['segment'] = !segmentInput
    ? null
    : segmentInput.type === 'list_segment'
      ? {
          type: 'list_segment',
          file: undefined,
        }
      : {
          type: 'condition_segment',
          condition: segmentInput.condition,
        };
  const parsed = MessageFormValueSchema.parse({
    messageCategoryKey: input.messageCategoryKey,
    name: input.name,
    title: input.title,
    summary: input.summary,
    notificationSettings: input.notificationSettings,
    segment: segmentSetting,
    thumbUrl: imageUploadField.init(input.thumbUrl),
    scheduleAt: input?.scheduleAt ? new Date(input.scheduleAt) : undefined,
    actionSetting: ActionDialogForm.toFormValue(input.actionSetting),
  });

  return parsed;
};
